import { ContentWrapper } from '@components'
import ErrorFallback from '@components/ErrorFallback'
import MaintenanceModal from '@components/MaintenanceModal'
import SuspenseFallback from '@components/SuspenseFallback'
import TestMethods from '@views/Test/TestMethods'
import { configuration, useLogErrorRequest } from 'mmfintech-checkout-commons'
import { MMGlobalSettingsProvider } from 'mmfintech-portal-commons'
import type { MMGlobalSettings } from 'mmfintech-portal-commons/types'
import { Suspense, useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Toaster } from 'react-hot-toast'
import { withTranslation } from 'react-i18next'
import { Redirect, Route, type RouteProps, BrowserRouter as Router, Switch } from 'react-router-dom'
import { ThemeProvider } from 'styled-components'
import routes from './routes'
import settings from './settings'
import theme from './theme'

const AppInner = () => {
  useEffect(() => {
    configuration.createBackendConfig(settings.backendForLocalhost)
    // configuration.createCardGatewayConfig(settings.)
  }, [])

  const mmGlobalSettings: Partial<MMGlobalSettings> = {
    buttonLoadingStyle: 'TailSpin',
    buttonLoadingColor: '#ffffff'
  }

  return (
    <MMGlobalSettingsProvider settings={mmGlobalSettings}>
      <ThemeProvider theme={theme}>
        <Router>
          <ContentWrapper>
            <Switch>
              {!configuration.isProduction() ? (
                <CustomRoute path='/methods' exact={true} component={TestMethods} />
              ) : null}
              {routes.map(route => {
                const { path, exact, redirect, component } = route
                if (redirect) {
                  return (
                    <Route key={path} exact={exact} path={path}>
                      <Redirect to={redirect} />
                    </Route>
                  )
                }

                if (component) {
                  return <CustomRoute key={path} path={path} exact={exact} component={component} />
                }

                return null
              })}
            </Switch>

            <MaintenanceModal />
          </ContentWrapper>

          <Toaster
            position='top-right'
            containerStyle={{
              top: '10rem'
            }}
            toastOptions={{
              className: '',
              style: {
                color: '#000000',
                fontFamily: 'inherit',
                fontSize: '1.4rem',
                fontStyle: 'normal',
                padding: '1.5rem',
                borderRadius: '0'
              },
              success: {}
            }}
          />
        </Router>
      </ThemeProvider>
    </MMGlobalSettingsProvider>
  )
}

const ThisApp = withTranslation()(AppInner)

const App = () => {
  const { logErrorRequest } = useLogErrorRequest()

  return (
    <Suspense fallback={<SuspenseFallback />}>
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={(error: Error, info: { componentStack: string }) =>
          logErrorRequest(error.toString(), info?.componentStack)
        }
        onReset={() => window.location.replace('/')}
      >
        <ThisApp />
      </ErrorBoundary>
    </Suspense>
  )
}

export default App

const CustomRoute = ({ component: Component, ...rest }: RouteProps) => (
  <Route {...rest} render={props => <Component {...props} />} />
)
